import React, { useEffect, useRef, useState } from 'react';
import { BuroPortal, money2 } from './helpers';
import { AvisoCodigoPostal, BuroCampo } from './BuroCampo';
import { BuroActividadSelect } from './BuroActividadSelect';
import { useAutollenadoDomicilio } from './useCodigoPostal';
import { clavesDomicilio } from './columnasPm';
import { AVISO_SIN_CLIENTE } from './helpers';
import { AYUDA, AYUDA_SOLO_REPORTE, DOMICILIO_PF, columnaApi, erroresDe, esAviso, nacimientoDeRfc, seEnviaComo, valorDe, verFecha } from './columnas';

/*
 * P4 · Panel lateral "Editar celda". Solo captura: guardar, confirmar y
 * restaurar suben a quien lo abre (BuroReporteTabla), que habla con la API y
 * cierra el panel. `error` es el 422 del backend, tal cual.
 *
 * props:
 *   fila, col      fila del reporte y definición de la columna (columnas.js)
 *   nContratos     contratos del mismo cliente en el reporte (texto del alcance)
 *   filas          todas las filas del reporte (sugerencias de colonia/ciudad/empresa)
 *   corregibles    columnas que admiten "corregir también en el sistema" (del backend)
 *   guardando      bloquea los botones mientras viaja el PATCH
 */
export const BuroCeldaEditor = ({ fila, col, nContratos, filas, corregibles, guardando, error, onClose, onGuardar, onConfirmar, onConfirmarAviso, onRestaurar, formato }) => {
  const esTipo = col.k === 'tipo_persona';
  const actual = valorDe(fila, col);
  const errores = erroresDe(fila, col);
  // Aviso de "confirmar con Buró" (VR/FR) relacionado con esta celda: puede estar
  // activo (en errores) o ya confirmado (en fila.confirmaciones, ya fuera de errores).
  const AVISO_COL = { pendiente_buro_vr: '3018', pendiente_buro_fr: '3022' };
  const avisoActivo = errores.find(esAviso);
  const reglaConfirmada = Object.keys(fila.confirmaciones || {}).find((r) => AVISO_COL[r] === String(col.k));
  const avisoCelda = avisoActivo || (reglaConfirmada ? { regla: reglaConfirmada } : null);
  const avisoConfirmado = avisoCelda && (fila.confirmaciones || {})[avisoCelda.regla] !== undefined;
  const fuente = (fila.fuentes || {})[col.k];
  const nota = (fila.notas || {})[col.k];
  const ayuda = col.ayuda || AYUDA[col.k === 'tipo_persona' ? 'tipo_persona' : Number(col.k)];
  const delSistema = esTipo ? '' : (fila.sugeridos || {})[col.k];

  const [borrador, setBorrador] = useState(actual);
  const [alcance, setAlcance] = useState('reporte');
  const [motivo, setMotivo] = useState('');

  const verSistema = (v) => (v === '' || v == null ? '(vacío)' : (col.kind === 'amount' ? money2(v) : v));
  const puedeRestaurar = fuente === 'manual' && delSistema != null && delSistema !== actual;
  // Solo las columnas que el backend declara corregibles, y nunca en un aval.
  // Sin cliente ligado nada se guarda en el sistema (ni el tipo de persona).
  const sinCliente = !fila.cliente_ligado;
  const corregible = !esTipo && !fila.aval && !sinCliente && (corregibles ? corregibles.includes(columnaApi(col)) : !!col.cl);
  const enSistema = corregible && alcance === 'sistema';
  // Domicilio: al abrir cualquier celda del domicilio (PF 17 a 23; PM/PFAE EM 13 a 19) sale el mismo bloque
  // completo (C.P., estado, municipio, ciudad, colonia, calle, complemento), editable y con lo que ya tiene
  // la fila. El C.P. encontrado llena estado, municipio, ciudad y colonia; calle y complemento nunca.
  const claves = col.segmento === 'EM' ? clavesDomicilio(col) : (!col.segmento && col.dom ? DOMICILIO_PF : null);
  const rolDom = claves ? col.dom : null;
  const colsDom = rolDom ? ['cp', 'estado', 'municipio', 'ciudad', 'colonia', 'calle', 'complemento'].map((r) => [r, (formato?.col || {})[claves[r]]]).filter(([, c]) => c) : [];
  const enDom = colsDom.length > 0 && colsDom.some(([, c]) => c.k === col.k);
  const [dom, setDom] = useState(() => (enDom ? Object.fromEntries(Object.values(claves).map((k) => [k, String((fila.valores || {})[k] ?? '')])) : {}));
  const cp = useAutollenadoDomicilio(enDom ? dom[claves.cp] : '', enDom ? claves : null, setDom);
  const datosDom = enDom && cp.estado === 'ok' ? cp.datos : null;
  // Obligatorios vacíos del domicilio: calle, colonia, estado y C.P.; municipio o ciudad (uno basta).
  const OBLIGATORIOS_DOM = ['calle', 'colonia', 'estado', 'cp'];
  const vacio = (r) => String(dom[claves?.[r]] ?? '').trim() === '';
  const falta = (r) => enDom && vacio(r) && (OBLIGATORIOS_DOM.includes(r) || (['municipio', 'ciudad'].includes(r) && vacio('municipio') && vacio('ciudad')));
  // Valor de la celda abierta: en el domicilio sale del bloque.
  const valor = enDom ? (dom[col.k] ?? '') : borrador;
  const envio = seEnviaComo(col, valor);
  // La celda abierta recibe el foco.
  const abierta = useRef(null);
  useEffect(() => { abierta.current?.querySelector('input,select')?.focus(); }, []);
  // Guardar manda en un solo lote todo lo del bloque que cambió respecto de la fila (incluida la celda abierta).
  const cambiosDom = () => colsDom
    .filter(([, c]) => dom[c.k] !== String((fila.valores || {})[c.k] ?? ''))
    .map(([, c]) => ({ columna: Number(c.k), valor: dom[c.k] }));
  // Nacimiento: sugerencia calculada desde el RFC de la fila.
  const rfc = (fila.valores || {})['10'];
  const delRfc = col.k === '9' ? nacimientoDeRfc(rfc) : '';
  const ayudaSolo = AYUDA_SOLO_REPORTE[col.k];
  const guardarDom = () => {
    const cambios = cambiosDom();
    guardar(valor, cambios.length ? { cambios } : {});
  };
  const guardar = (valor, extra = {}) => onGuardar({
    columna: columnaApi(col),
    valor,
    // El tipo de persona siempre se guarda en el sistema (lo hace el backend).
    aplicarEnSistema: esTipo ? !sinCliente : enSistema,
    motivo,
    ...extra,
  });

  return (
    <BuroPortal>
      <div className="bu-panel" style={{ width: 420 }}>
        <div className="bu-panel-head">
          <div style={{ flex: 1 }}>
            <h3>{col.label} <span className="n">· columna {col.num}</span></h3>
            <div className="s">{fila.numero_cuenta} · {fila.cliente}</div>
          </div>
          <button type="button" className="bu-icon-btn" onClick={onClose} aria-label="Cerrar"><i className="pi pi-times" /></button>
        </div>

        <div className="bu-panel-body">
          {error && <div className="bu-note bu-note-err"><i className="pi pi-exclamation-triangle" /><span>{error}</span></div>}
          {errores.map((e) => (esAviso(e) ? (
            <div key={`${e.columna}-${e.regla}`} className="bu-note bu-note-warn">
              <i className="pi pi-info-circle" /><span>Pendiente de confirmar con Buró (no es un dato faltante): {e.mensaje}</span>
            </div>
          ) : (
            <div key={`${e.columna}-${e.regla}`} className="bu-note bu-note-err">
              <i className="pi pi-exclamation-triangle" /><span>{e.mensaje}</span>
            </div>
          )))}
          {avisoConfirmado && !avisoActivo && (
            <div className="bu-note bu-note-ok"><i className="pi pi-check-circle" /><span>Confirmado con Buró. Ya no bloquea el cierre (se revierte si cambias el valor).</span></div>
          )}
          {fuente === 'sugerencia' && (
            <div className="bu-note bu-note-warn">
              <i className="pi pi-info-circle" /><span>{nota || 'Calculado por el sistema. Confírmalo.'}</span>
            </div>
          )}
          {fuente === 'falta' && !errores.length && nota && (
            <div className="bu-note bu-note-err"><i className="pi pi-exclamation-triangle" /><span>{nota}</span></div>
          )}
          {ayuda && (
            <div className="bu-sub" style={{ fontSize: 13, lineHeight: 1.45, display: 'flex', gap: 10 }}>
              <i className="pi pi-question-circle" style={{ marginTop: 2 }} /><span>{ayuda}</span>
            </div>
          )}

          <div className="bu-grid2">
            <div className="bu-label">Valor del sistema</div>
            <div className="tnum">{verSistema(delSistema)}</div>
            {!enDom && <div className="bu-label">Valor para Buró</div>}
            {!enDom && <div>
              <BuroCampo
                col={col}
                value={borrador}
                onChange={setBorrador}
                filas={filas}
                estado={(fila.valores || {})['22']}
              />
              {col.k === '9' && delRfc && delRfc !== borrador && (
                <div className="bu-note bu-note-warn" style={{ marginTop: 8, alignItems: 'center' }}>
                  <i className="pi pi-info-circle" style={{ marginTop: 0 }} />
                  <span style={{ flex: 1 }}>Sugerida desde el RFC: {verFecha(delRfc)}</span>
                  <button type="button" className="bu-btn bu-btn-sug" style={{ height: 28 }} onClick={() => setBorrador(delRfc)}>Usar</button>
                </div>
              )}
              {col.k === '9' && !rfc && !borrador && (
                <div className="bu-sub" style={{ fontSize: 12, marginTop: 6 }}>Sin RFC: captúrala del INE.</div>
              )}
              {/* EM10/EM11/EM12 de PM: qué es la actividad, buscador SCIAN y "Otro". */}
              {['1010', '1011', '1012'].includes(String(col.k)) && (
                <BuroActividadSelect value={borrador} onChange={setBorrador} disabled={guardando} />
              )}
              {/* CR21 de PM: sin incumplimiento = 8 ceros (manual Buró Empresas v5). El input de fecha no los puede mostrar. */}
              {String(col.k) === '3021' && (
                <div className="bu-note bu-note-info" style={{ marginTop: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                  <i className="pi pi-info-circle" style={{ marginTop: 0 }} />
                  <span style={{ flex: '1 1 160px', minWidth: 0 }}>
                    {borrador === '00000000'
                      ? 'Sin incumplimiento: se enviará 00000000. Con días vencidos, Buró la rechaza.'
                      : 'Si nunca se ha atrasado, se reporta 00000000.'}
                  </span>
                  <button type="button" className="bu-btn bu-btn-sug" style={{ height: 28, whiteSpace: 'nowrap' }} disabled={guardando || borrador === '00000000'} onClick={() => setBorrador('00000000')}>Sin incumplimiento</button>
                </div>
              )}
            </div>}
            <div className="bu-label">Se enviará como</div>
            <div className="bu-mono">{envio === '' ? '(vacío)' : (col.kind === 'amount' ? Number(envio).toLocaleString('en-US') : envio)}</div>
          </div>

          {enDom && (
            <div className="bu-field" style={{ gap: 10 }}>
              <div className="bu-label">Domicilio {col.segmento === 'EM' ? 'de la empresa' : (fila.aval ? 'del aval' : 'del cliente')}</div>
              {colsDom.map(([r, c]) => (
                <React.Fragment key={c.k}>
                  <label className="bu-field" ref={c.k === col.k ? abierta : undefined}>
                    <span className="bu-label">{c.label} · {c.num}{(c.obligatorio ?? OBLIGATORIOS_DOM.includes(r)) ? ' *' : ''}</span>
                    <BuroCampo
                      col={c}
                      value={dom[c.k] ?? ''}
                      onChange={(v) => setDom((prev) => ({ ...prev, [c.k]: v }))}
                      filas={filas}
                      estado={dom[claves.estado]}
                      disabled={guardando}
                      invalid={falta(r)}
                      className={falta(r) ? 'bu-input bu-req-falta' : 'bu-input'}
                      colonias={r === 'colonia' && datosDom ? datosDom.colonias : null}
                    />
                    {r === 'cp' && cp.estado !== 'ok' && <AvisoCodigoPostal cp={cp} />}
                  </label>
                  {r === 'cp' && datosDom && (
                    <div className="bu-note bu-note-info" style={{ fontSize: 12 }}>
                      <i className="pi pi-info-circle" />
                      <span>C.P. encontrado: {[datosDom.estado, datosDom.municipio, datosDom.ciudad].filter(Boolean).join(' · ')}</span>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {esTipo && borrador && (formato?.tipo === 'pm_pfae' ? borrador === 'PF' : borrador !== 'PF') && (
            <div className="bu-note bu-note-info">
              <i className="pi pi-info-circle" />Va en el reporte de {borrador === 'PF' ? 'Personas Físicas' : 'Buró Empresas'}. La fila saldrá de este reporte.
            </div>
          )}
          {sinCliente && (esTipo || (corregibles ? corregibles.includes(columnaApi(col)) : col.cl)) && (
            <div className="bu-note bu-note-warn"><i className="pi pi-info-circle" />{esTipo ? 'Este contrato no tiene cliente ligado. El tipo se guarda en el contrato.' : `${AVISO_SIN_CLIENTE}.`}</div>
          )}
          {ayudaSolo && (
            <div className="bu-note bu-note-info"><i className="pi pi-info-circle" />{ayudaSolo}</div>
          )}
          {col.kind === 'amount' && !corregible && (
            <div className="bu-note bu-note-warn">
              <i className="pi pi-info-circle" />Para cambiar un monto de forma permanente, corrige el pago en Cartera y usa Regenerar.
            </div>
          )}

          {!esTipo && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label className="bu-radio">
                <input type="radio" name="scope" checked={!corregible || alcance === 'reporte'} onChange={() => setAlcance('reporte')} />
                Solo en este reporte
              </label>
              {corregible && (
              <label className="bu-radio" style={{ alignItems: 'flex-start' }}>
                <input type="radio" name="scope" style={{ marginTop: 2 }} checked={alcance === 'sistema'} onChange={() => setAlcance('sistema')} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span>Corregir también en el sistema</span>
                  <span className="bu-sub" style={{ fontSize: 12 }}>
                    Se actualiza en {nContratos === 1 ? 'su contrato' : `sus ${nContratos} contratos`} y en los siguientes reportes.
                  </span>
                </span>
              </label>
              )}
            </div>
          )}

          <div className="bu-field">
            <div className="bu-label">Motivo (opcional)</div>
            <input className="bu-input" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>

          {fuente === 'manual' && (
            <div style={{ color: '#0E5E80', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="pi pi-pencil" style={{ fontSize: 11 }} />Editado a mano
            </div>
          )}
        </div>

        <div className="bu-panel-foot">
          {!esTipo && (
            <button type="button" className="bu-btn bu-btn-link" disabled={!puedeRestaurar || guardando} onClick={() => onRestaurar(columnaApi(col), delSistema)}>
              Restaurar valor del sistema
            </button>
          )}
          <div className="bu-ml" style={{ display: 'flex', gap: 8 }}>
            {fuente === 'sugerencia' && (
              <button type="button" className="bu-btn bu-btn-sug" disabled={guardando} onClick={() => onConfirmar(columnaApi(col))}>
                Confirmar sugerencia
              </button>
            )}
            {avisoCelda && onConfirmarAviso && (
              <button type="button" className="bu-btn bu-btn-sug" disabled={guardando}
                onClick={() => onConfirmarAviso(fila.id, avisoCelda.regla, !avisoConfirmado)}>
                {avisoConfirmado ? 'Quitar confirmación' : 'Confirmar con Buró'}
              </button>
            )}
            <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
            <button type="button" className="bu-btn bu-btn-primary" disabled={guardando} onClick={() => (enDom ? guardarDom() : guardar(borrador))}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </BuroPortal>
  );
};

// 'YYYY-MM-DD' → 'DD/MM/YYYY' (las fechas de `recuperacion` no vienen en formato Buró).
const verFechaIso = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v ?? '');
};

// Monto para el campo de texto: sin ceros de más ni notación científica.
const montoTxt = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? '' : String(Math.round(Number(v) * 100) / 100));

// El auto cubre la deuda: no hay nada que elegir. Misma tolerancia que el backend.
const cubreDeuda = (r) => Number(r.faltante) <= 0.005;

/*
 * Distintivo de la celda 58 en las filas de auto recuperado: el resultado
 * efectivo de esa persona (el del titular si el aval sigue al titular).
 * Devuelve { texto, clase, titulo }.
 */
export const distintivoRecuperacion = (r) => {
  const persona = r.persona === 'aval' ? 'aval' : 'titular';
  const forma = r.forma === 'juicio' ? 'por juicio' : 'lo entregó el cliente';
  const sigue = r.resultado_guardado === 'igual' ? ' · igual que el titular' : '';
  if (r.resultado === 'cubre') {
    return { texto: 'Cubre', clase: 'ok', titulo: `Auto recuperado (${forma}) · ${persona}: el auto cubre la deuda${sigue}` };
  }
  if (r.resultado === 'cobra') {
    const monto = money2(r.monto_pendiente ?? r.faltante);
    return r.pagado
      ? { texto: 'Pagado', clase: 'ok', titulo: `Auto recuperado (${forma}) · ${persona}: se siguió cobrando ${monto} y se pagó el ${verFechaIso(r.fecha_pago)}${sigue}` }
      : { texto: 'Sigue cobrando', clase: 'cobra', titulo: `Auto recuperado (${forma}) · ${persona}: sigue cobrando ${monto}${sigue}` };
  }
  return { texto: 'Perdona', clase: '', titulo: `Auto recuperado (${forma}) · ${persona}: se perdona lo que falta (${money2(r.faltante)})${sigue}` };
};

/*
 * Panel "Resultado de la recuperación": sustituye al editor de texto en la
 * celda 58 de las filas de auto recuperado (fila.recuperacion != null). Igual
 * que BuroCeldaEditor, solo captura: guardar sube a BuroReporteTabla, que habla
 * con la API (resultadoRecuperacion) y cierra el panel. `error` es el 422 del backend.
 *
 * props: fila · guardando · error · onClose
 *   onGuardar({forma, resultado?, monto_pendiente?})   resultado solo si el auto no cubre la deuda
 *   onEditarCelda()   salida al editor de texto normal (confirmar la sugerencia o escribir la clave)
 */
export const BuroRecuperacionEditor = ({ fila, guardando, error, onClose, onGuardar, onEditarCelda, codigoResultado = '58' }) => {
  const r = fila.recuperacion;
  const esAval = r.persona === 'aval';
  const cubre = cubreDeuda(r);
  const guardado = r.resultado_guardado;
  // Lo guardado, si es una opción válida para esta persona; si no (recuperación
  // vieja o ya cubierta): el aval sigue al titular y el titular perdona.
  const inicial = guardado === 'perdona' || guardado === 'cobra' || (esAval && guardado === 'igual')
    ? guardado
    : (esAval ? 'igual' : 'perdona');

  const [forma, setForma] = useState(r.forma === 'juicio' ? 'juicio' : 'entrega');
  const [resultado, setResultado] = useState(inicial);
  // Por omisión el monto guardado; si no hay, lo que falta por cubrir.
  const [monto, setMonto] = useState(montoTxt(r.monto_pendiente ?? r.faltante));
  const [errLocal, setErrLocal] = useState(null);

  // PM: la clave 3019 y las celdas que cambian con ella (3011–3022); PF solo la 58.
  const errores = erroresDe(fila, codigoResultado === '3019' ? { k: '3011', hasta: 3022 } : { k: codigoResultado });
  const fuente = (fila.fuentes || {})[codigoResultado];
  const nota = (fila.notas || {})[codigoResultado];
  // Solo mientras no se cambia la elección: si ya pagó, el pago se maneja en Cartera.
  const pagadoVigente = r.pagado && r.resultado === 'cobra' && resultado === inicial;

  const guardar = () => {
    if (!cubre && resultado === 'cobra' && !(Number(monto) > 0)) {
      setErrLocal('Indica el monto pendiente que se sigue cobrando (mayor a cero).');
      return;
    }
    setErrLocal(null);
    const body = { forma };
    if (!cubre) {
      body.resultado = resultado;
      if (resultado === 'cobra') body.monto_pendiente = Math.round(Number(monto) * 100) / 100;
    }
    onGuardar(body);
  };

  const opcion = (valor, titulo, detalle) => (
    <label key={valor} className="bu-radio" style={{ alignItems: 'flex-start' }}>
      <input type="radio" name="resultado-recuperacion" style={{ marginTop: 2 }} checked={resultado === valor} onChange={() => setResultado(valor)} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span>{titulo}</span>
        <span className="bu-sub" style={{ fontSize: 12 }}>{detalle}</span>
      </span>
    </label>
  );

  return (
    <BuroPortal>
      <div className="bu-panel" style={{ width: 420 }}>
        <div className="bu-panel-head">
          <div style={{ flex: 1 }}>
            <h3>Auto recuperado <span className="n">· columna {codigoResultado}</span></h3>
            <div className="s">{fila.numero_cuenta} · {fila.cliente} · {esAval ? 'Aval' : 'Titular'}</div>
          </div>
          <button type="button" className="bu-icon-btn" onClick={onClose} aria-label="Cerrar"><i className="pi pi-times" /></button>
        </div>

        <div className="bu-panel-body">
          {(errLocal || error) && <div className="bu-note bu-note-err"><i className="pi pi-exclamation-triangle" /><span>{errLocal || error}</span></div>}
          {errores.map((e) => (esAviso(e) ? (
            <div key={`${e.columna}-${e.regla}`} className="bu-note bu-note-warn">
              <i className="pi pi-info-circle" /><span>Pendiente de confirmar con Buró (no es un dato faltante): {e.mensaje}</span>
            </div>
          ) : (
            <div key={`${e.columna}-${e.regla}`} className="bu-note bu-note-err">
              <i className="pi pi-exclamation-triangle" /><span>{e.mensaje}</span>
            </div>
          )))}
          {fuente === 'sugerencia' && nota && (
            <div className="bu-note bu-note-warn"><i className="pi pi-info-circle" /><span>{nota}</span></div>
          )}

          <div className="bu-grid2">
            <div className="bu-label">Valor del auto</div>
            <div className="tnum">{r.valor_auto == null ? 'Sin valor' : money2(r.valor_auto)}</div>
            <div className="bu-label">Falta por cubrir</div>
            <div className="tnum">{money2(r.faltante)}</div>
          </div>

          <div className="bu-field" style={{ gap: 10 }}>
            <div className="bu-label">¿Cómo se recuperó?</div>
            <label className="bu-radio">
              <input type="radio" name="forma-recuperacion" checked={forma === 'entrega'} onChange={() => setForma('entrega')} />
              Lo entregó el cliente <span className="bu-sub">· VR</span>
            </label>
            <label className="bu-radio">
              <input type="radio" name="forma-recuperacion" checked={forma === 'juicio'} onChange={() => setForma('juicio')} />
              Por juicio <span className="bu-sub">· FR</span>
            </label>
          </div>

          <div className="bu-field" style={{ gap: 10 }}>
            <div className="bu-label">Resultado para {esAval ? 'el aval' : 'el titular'}</div>
            {cubre ? (
              <div className="bu-note bu-note-ok">
                <i className="pi pi-check-circle" /><span>El auto cubre la deuda. No hay nada que elegir.</span>
              </div>
            ) : (
              <>
                {esAval && opcion('igual', 'Igual que el titular', 'Sigue lo que se elija para el titular.')}
                {opcion('perdona', 'Perdonar', `Se perdona lo que falta (${money2(r.faltante)}).`)}
                {opcion('cobra', 'Seguir cobrando', 'Queda un monto pendiente por cobrar.')}
                {resultado === 'cobra' && (
                  <div className="bu-field">
                    <div className="bu-label">Monto pendiente</div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="bu-input"
                      value={monto}
                      onChange={(e) => setMonto(e.target.value)}
                    />
                    <div className="bu-sub" style={{ fontSize: 12 }}>
                      Por omisión, lo que falta por cubrir. Mientras no se pague, la cuenta sigue abierta en Buró.
                    </div>
                  </div>
                )}
              </>
            )}
            {pagadoVigente && (
              <div className="bu-note bu-note-ok">
                <i className="pi pi-check-circle" />
                <span>
                  {guardado === 'igual' ? 'El titular ya pagó' : 'Pagado'} el {verFechaIso(r.fecha_pago)}. El pago se cambia desde Cartera.
                </span>
              </div>
            )}
          </div>

          <div className="bu-note bu-note-info">
            <i className="pi pi-info-circle" />
            <span>
              Se guarda en el contrato (también se ve en Cartera). Las celdas de la cuenta de este contrato que hayas editado a mano se vuelven a calcular.
            </span>
          </div>
        </div>

        <div className="bu-panel-foot">
          <button type="button" className="bu-btn bu-btn-link" disabled={guardando} onClick={onEditarCelda}>
            Editar o confirmar la celda
          </button>
          <div className="bu-ml" style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
            <button type="button" className="bu-btn bu-btn-primary" disabled={guardando} onClick={guardar}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </BuroPortal>
  );
};

export default BuroCeldaEditor;
